import { ArrowLeft, Archive, BookOpen, CheckCircle2, Copy, Image, Pencil, Save, Trash2, UploadCloud, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { Modal } from '../../components/common/Modal';
import { StatusBadge } from '../../components/common/StatusBadge';
import { COURSE_TYPES, getCourseTypeLabel } from '../../utils/courseTypes';
import { DURATION_UNITS, PAYMENT_TERMS, formatCourseDuration, parseCourseDuration, recommendedPaymentTerm } from '../../utils/courseBilling';
import defaultCourseImage from '../../assets/newimage29-optimized.webp';
import { storage } from '../../firebase/firebase';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import {
  archiveCourse,
  createCurriculumEntry,
  deleteCourse,
  deleteCurriculumEntry,
  getCourse,
  getCurriculumEntries,
  getMembers,
  replaceCurriculumEntries,
  updateCurriculumEntry,
  updateCurriculumWeekObjective,
  updateCourseInstructor,
  updateCourse,
  updateCoursePublicEnrollment,
} from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency } from '../../utils/currency';
import { buildCurriculumPrompt, groupCurriculumEntries, parseCurriculumJson } from '../../utils/curriculum';

const WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

function buildWeekDayRow(week, dayName) {
  const existingEntry = week.entries.find((entry) => entry.dayName === dayName);
  return existingEntry || {
    weekLabel: week.weekLabel,
    dayName,
    dayLabel: `${week.weekLabel} - ${dayName}`,
    topic: '',
    objective: '',
    learningActivities: '',
    goals: '',
    weeklyObjective: week.weeklyObjective || '',
  };
}

function CurriculumTableRow({ entry, weekLabel, dayName, onSave, onRemove, saving }) {
  const [draft, setDraft] = useState({
    weekLabel: entry.weekLabel || weekLabel,
    dayName: entry.dayName || dayName,
    dayLabel: entry.dayLabel || `${weekLabel} - ${dayName}`,
    topic: entry.topic || '',
    objective: entry.objective || '',
    learningActivities: entry.learningActivities || '',
    goals: entry.goals || '',
    weeklyObjective: entry.weeklyObjective || '',
  });

  useEffect(() => {
    setDraft({
      weekLabel: entry.weekLabel || weekLabel,
      dayName: entry.dayName || dayName,
      dayLabel: entry.dayLabel || `${weekLabel} - ${dayName}`,
      topic: entry.topic || '',
      objective: entry.objective || '',
      learningActivities: entry.learningActivities || '',
      goals: entry.goals || '',
      weeklyObjective: entry.weeklyObjective || '',
    });
  }, [
    dayName,
    entry.dayLabel,
    entry.dayName,
    entry.goals,
    entry.learningActivities,
    entry.objective,
    entry.topic,
    entry.weekLabel,
    entry.weeklyObjective,
    weekLabel,
  ]);

  return (
    <tr className="curriculum-table-row">
      <td><strong>{weekLabel}</strong></td>
      <td><strong>{dayName}</strong></td>
      <td>
        <input
          className="table-input"
          id={`topic-${entry.id || `${weekLabel}-${dayName}`}`}
          value={draft.topic || ''}
          onChange={(event) => setDraft({ ...draft, topic: event.target.value })}
          placeholder="Topic"
        />
      </td>
      <td>
        <textarea
          className="table-input"
          id={`objective-${entry.id || `${weekLabel}-${dayName}`}`}
          value={draft.objective || ''}
          onChange={(event) => setDraft({ ...draft, objective: event.target.value })}
          placeholder="Objective"
        />
      </td>
      <td>
        <textarea
          className="table-input"
          id={`activities-${entry.id || `${weekLabel}-${dayName}`}`}
          value={draft.learningActivities || ''}
          onChange={(event) => setDraft({ ...draft, learningActivities: event.target.value })}
          placeholder="Learning activities"
        />
      </td>
      <td>
        <textarea
          className="table-input"
          id={`goals-${entry.id || `${weekLabel}-${dayName}`}`}
          value={draft.goals || ''}
          onChange={(event) => setDraft({ ...draft, goals: event.target.value })}
          placeholder="Goals"
        />
      </td>
      <td className="table-actions">
        <Button type="button" onClick={() => onSave(entry, draft)} loading={saving} icon={Save}>Save</Button>
        {entry.id ? <Button type="button" variant="ghost" onClick={() => onRemove(entry.id)} disabled={saving}>Remove</Button> : null}
      </td>
    </tr>
  );
}

function CurriculumWeekTable({ week, onSaveRow, onRemoveRow, onSaveWeekObjective, saving }) {
  const [weeklyObjective, setWeeklyObjective] = useState(week.weeklyObjective || '');
  const weekRows = WEEK_DAYS.map((dayName) => buildWeekDayRow(week, dayName));

  useEffect(() => {
    setWeeklyObjective(week.weeklyObjective || '');
  }, [week.weeklyObjective]);

  return (
    <section className="week-block">
      <div className="panel-heading">
        <div>
          <h3>{week.weekLabel}</h3>
          <p>Monday to Friday</p>
        </div>
      </div>
      <div className="curriculum-week-grid">
        <div className="curriculum-table-wrap">
          <table className="curriculum-table">
            <thead>
              <tr>
                <th>Week</th>
                <th>Day</th>
                <th>Topic</th>
                <th>Objective</th>
                <th>Learning Activities</th>
                <th>Goals</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {weekRows.map((entry) => (
                <CurriculumTableRow
                  key={entry.id || `${week.weekLabel}-${entry.dayName}`}
                  entry={entry}
                  weekLabel={week.weekLabel}
                  dayName={entry.dayName}
                  onSave={onSaveRow}
                  onRemove={onRemoveRow}
                  saving={saving}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="week-summary">
        <FormField label="Weekly Objective" id={`weekly-objective-${week.weekLabel}`}>
          <textarea
            className="table-input"
            id={`weekly-objective-${week.weekLabel}`}
            value={weeklyObjective}
            onChange={(event) => setWeeklyObjective(event.target.value)}
            placeholder="Summarize the objective for the whole week"
          />
        </FormField>
        <div className="week-summary-actions">
          <Button type="button" variant="secondary" icon={Save} onClick={() => onSaveWeekObjective(week.weekLabel, weeklyObjective)} loading={saving}>Save Weekly Objective</Button>
        </div>
      </div>
    </section>
  );
}

export function CourseDetailPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { activeTenant, activeTenantId } = useTenant();
  const { notify } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [course, setCourse] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editValues, setEditValues] = useState({ name: '', code: '', description: '', durationValue: '', durationUnit: 'weeks', feeAmount: '', courseType: 'live_online', paymentTerm: 'weekly', imageURL: defaultCourseImage });
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [curriculum, setCurriculum] = useState([]);
  const [members, setMembers] = useState([]);
  const [assignValues, setAssignValues] = useState({ instructorId: '', instructorName: '' });
  const [importJsonText, setImportJsonText] = useState('');
  const [promptValues, setPromptValues] = useState({ targetLearners: '', focusAreas: '', extraInstructions: '' });
  const [jsonError, setJsonError] = useState('');
  const curriculumPrompt = useMemo(() => buildCurriculumPrompt(course, activeTenant, curriculum, promptValues), [activeTenant, course, curriculum, promptValues]);
  const weekBlocks = useMemo(() => groupCurriculumEntries(curriculum), [curriculum]);

  const load = useCallback(async () => {
    setLoading(true);
    const nextCourse = await getCourse(activeTenantId, courseId);
    if (!nextCourse) {
      setCourse(null);
      setCurriculum([]);
      setMembers([]);
      setLoading(false);
      return;
    }
    const nextCurriculum = await getCurriculumEntries(activeTenantId, courseId);
    let nextMembers = [];
    if (activeTenant?.type !== 'private_instructor') {
      try {
        nextMembers = await getMembers(activeTenantId);
      } catch (error) {
        console.warn('Unable to load members for instructor assignment', error);
      }
    }
    setCourse(nextCourse);
    const duration = parseCourseDuration(nextCourse.duration, nextCourse.durationValue, nextCourse.durationUnit);
    setEditValues({
      name: nextCourse.name || '',
      code: nextCourse.code || '',
      description: nextCourse.description || '',
      duration: nextCourse.duration || '',
      feeAmount: nextCourse.feeAmount ?? '',
      durationValue: duration.amount,
      durationUnit: duration.unit,
      courseType: nextCourse.courseType || 'live_online',
      paymentTerm: nextCourse.courseType === 'self_paced' ? 'full' : (nextCourse.paymentTerm || recommendedPaymentTerm(duration.amount, duration.unit)),
      imageURL: nextCourse.imageURL || defaultCourseImage,
    });
    setCurriculum(nextCurriculum);
    setMembers(nextMembers);
    setAssignValues({ instructorId: nextCourse.instructorId || '', instructorName: nextCourse.instructorName || '' });
    setLoading(false);
  }, [activeTenant?.type, activeTenantId, courseId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  async function togglePublic() {
    if (!course) return;
    setSaving(true);
    try {
      await updateCoursePublicEnrollment(activeTenantId, course.id, !course.publicEnrollment?.enabled);
      notify(course.publicEnrollment?.enabled ? 'Course removed from public page.' : 'Course published on your academy page.');
      await load();
    } catch (error) {
      console.error(error);
      notify('Unable to update course publishing. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function archiveThisCourse() {
    if (!course) return;
    setSaving(true);
    try {
      await archiveCourse(activeTenantId, course.id);
      notify('Course archived.');
      await load();
    } catch (error) {
      console.error(error);
      notify('Unable to archive course. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function deleteThisCourse() {
    if (!course) return;
    const confirmed = window.confirm('Delete this course? It will be hidden from the academy and dashboard.');
    if (!confirmed) return;
    setSaving(true);
    try {
      await deleteCourse(activeTenantId, course.id);
      notify('Course deleted.');
      navigate('/dashboard/courses', { replace: true });
    } catch (error) {
      console.error(error);
      notify('Unable to delete course. Try again.', 'error');
      setSaving(false);
    }
  }

  async function assignInstructor(event) {
    event.preventDefault();
    if (!course) return;
    setSaving(true);
    try {
      await updateCourseInstructor(activeTenantId, course.id, assignValues);
      notify('Instructor assigned to course.');
      await load();
    } catch (error) {
      console.error(error);
      notify('Unable to assign instructor. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function saveCourseDetails(event) {
    event.preventDefault();
    if (!course) return;
    setSaving(true);
    try {
      let imageURL = editValues.imageURL || defaultCourseImage;
      if (imageFile) {
        setUploadingImage(true);
        const safeName = imageFile.name.replace(/[^a-zA-Z0-9._-]/g, '-');
        const imageRef = ref(storage, `tenants/${activeTenantId}/courses/${course.id}/${Date.now()}-${safeName}`);
        const snapshot = await uploadBytes(imageRef, imageFile, { contentType: imageFile.type });
        imageURL = await getDownloadURL(snapshot.ref);
      }
      await updateCourse(activeTenantId, course.id, { ...editValues, imageURL, duration: formatCourseDuration(editValues.durationValue, editValues.durationUnit) });
      notify('Course details updated.');
      setEditOpen(false);
      setImageFile(null);
      await load();
    } catch (error) {
      console.error(error);
      notify('Unable to update course details. Try again.', 'error');
    } finally {
      setUploadingImage(false);
      setSaving(false);
    }
  }

  async function removeCurriculumEntry(entryId) {
    const confirmed = window.confirm('Remove this curriculum entry?');
    if (!confirmed) return;
    setSaving(true);
    try {
      await deleteCurriculumEntry(activeTenantId, course.id, entryId);
      notify('Curriculum entry removed.');
      await load();
    } catch (error) {
      console.error(error);
      notify('Unable to remove curriculum entry. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function copyCurriculumPrompt() {
    try {
      await navigator.clipboard.writeText(curriculumPrompt);
      notify('Curriculum prompt copied.');
    } catch (error) {
      console.error(error);
      notify('Unable to copy prompt. Please copy it manually.', 'error');
    }
  }

  async function importCurriculum(event) {
    event.preventDefault();
    if (!course) return;
    setJsonError('');
    setSaving(true);
    try {
      const entries = parseCurriculumJson(importJsonText);
      if (!entries.length) {
        throw new Error('Paste at least one curriculum entry.');
      }
      await replaceCurriculumEntries(activeTenantId, course.id, entries);
      notify('Curriculum JSON imported successfully.');
      setImportJsonText('');
      await load();
    } catch (error) {
      console.error(error);
      setJsonError(error.message || 'Unable to import curriculum JSON.');
    } finally {
      setSaving(false);
    }
  }

  async function saveCurriculumBlock(entry, draft) {
    if (!course) return;
    setSaving(true);
    try {
      const payload = {
        ...draft,
        weekLabel: draft.weekLabel || entry.weekLabel,
        dayName: draft.dayName || entry.dayName,
        dayLabel: draft.dayLabel || entry.dayLabel || `${draft.weekLabel || entry.weekLabel} - ${draft.dayName || entry.dayName}`,
        weeklyObjective: draft.weeklyObjective || entry.weeklyObjective || '',
      };

      if (entry.id) {
        await updateCurriculumEntry(activeTenantId, course.id, entry.id, payload);
      } else {
        await createCurriculumEntry(activeTenantId, course.id, payload);
      }
      notify('Curriculum block updated.');
      await load();
    } catch (error) {
      console.error(error);
      notify('Unable to update curriculum block. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function saveWeekObjective(weekLabel, weeklyObjective) {
    if (!course) return;
    setSaving(true);
    try {
      await updateCurriculumWeekObjective(activeTenantId, course.id, weekLabel, weeklyObjective);
      notify('Weekly objective updated.');
      await load();
    } catch (error) {
      console.error(error);
      notify('Unable to update weekly objective. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  const instructorMembers = members.filter((member) => ['instructor', 'administrator', 'owner'].includes(member.roleId));
  const isPrivateInstructor = activeTenant?.type === 'private_instructor';
  const currency = activeTenant?.settings?.currency || 'KES';

  if (loading) return <LoadingState label="Loading course..." />;

  if (!course) {
    return (
      <EmptyState
        title="Course not found"
        message="This course may have been deleted or is no longer available."
        actionLabel="Back to Courses"
        to="/dashboard/courses"
        icon={BookOpen}
      />
    );
  }

  return (
    <div className="page-stack course-detail">
      <div className="course-detail-top">
        <Button to="/dashboard/courses" variant="secondary" icon={ArrowLeft}>Back to Courses</Button>
        <div className="course-detail-actions">
          <Button className="course-edit-button" variant="secondary" onClick={() => setEditOpen(true)} icon={Pencil}>Edit course</Button>
          <Button className="course-publish-button" variant="secondary" onClick={togglePublic}>{course.publicEnrollment?.enabled ? 'Unpublish' : 'Publish'}</Button>
          <Button className="course-archive-button" variant="secondary" onClick={archiveThisCourse} icon={Archive}>Archive</Button>
          <Button className="course-delete-button" variant="secondary" onClick={deleteThisCourse} icon={Trash2}>Delete</Button>
        </div>
      </div>

      <section className="course-hero panel">
        <img className="course-hero-image" src={course.imageURL || defaultCourseImage} alt="" />
        <div className="course-hero-intro">
          <span className="course-hero-mark"><BookOpen size={23} strokeWidth={2.15} /></span>
          <div>
            <p className="eyebrow">Course workspace</p>
            <h1>{course.name}</h1>
            <p>{course.description || 'No description added yet.'}</p>
          </div>
        </div>
        <div className="course-hero-meta">
          <div><span>Code</span><strong>{course.code}</strong></div>
          <div><span>Duration</span><strong>{course.duration || '-'}</strong></div>
          <div><span>Fee</span><strong>{formatCurrency(course.feeAmount || 0, currency)}</strong></div>
          <div><span>Course type</span><strong>{getCourseTypeLabel(course.courseType)}</strong></div>
          <div><span>Payment term</span><strong>{PAYMENT_TERMS.find((term) => term.value === course.paymentTerm)?.label || 'Payment term not set'}</strong></div>
          <div><span>Status</span><StatusBadge status={course.status} /></div>
        </div>
      </section>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit course"
        description="Update the details learners see for this course."
        className="course-create-modal course-edit-modal"
      >
        <form onSubmit={saveCourseDetails} className="modal-form">
          <FormField label="Course Name" id="edit-course-name"><input id="edit-course-name" required value={editValues.name} onChange={(event) => setEditValues({ ...editValues, name: event.target.value })} /></FormField>
          <FormField label="Course Code" id="edit-course-code"><input id="edit-course-code" required value={editValues.code} onChange={(event) => setEditValues({ ...editValues, code: event.target.value.toUpperCase() })} /></FormField>
          <div className="form-grid">
            <FormField label="Course duration" id="edit-course-duration-value"><input id="edit-course-duration-value" type="number" min="1" step="1" required placeholder="12" value={editValues.durationValue} onChange={(event) => { const durationValue = event.target.value; setEditValues({ ...editValues, durationValue, paymentTerm: recommendedPaymentTerm(durationValue, editValues.durationUnit) }); }} /></FormField>
            <FormField label="Duration unit" id="edit-course-duration-unit"><select id="edit-course-duration-unit" required value={editValues.durationUnit} onChange={(event) => { const durationUnit = event.target.value; setEditValues({ ...editValues, durationUnit, paymentTerm: recommendedPaymentTerm(editValues.durationValue, durationUnit) }); }}>{DURATION_UNITS.map((unit) => <option key={unit.value} value={unit.value}>{unit.label}</option>)}</select></FormField>
            <FormField label={`Price / Fee (${currency})`} id="edit-course-fee"><input id="edit-course-fee" type="number" min="0" step="1" required value={editValues.feeAmount} onChange={(event) => setEditValues({ ...editValues, feeAmount: event.target.value })} /></FormField>
          </div>
          <FormField label="Course type" id="edit-course-type"><select id="edit-course-type" required value={editValues.courseType} onChange={(event) => { const courseType = event.target.value; setEditValues({ ...editValues, courseType, paymentTerm: courseType === 'self_paced' ? 'full' : editValues.paymentTerm }); }}>{COURSE_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select><small>{COURSE_TYPES.find((type) => type.value === editValues.courseType)?.description}</small></FormField>
          <FormField label="Fee payment term" id="edit-course-payment-term"><select id="edit-course-payment-term" required disabled={editValues.courseType === 'self_paced'} value={editValues.courseType === 'self_paced' ? 'full' : editValues.paymentTerm} onChange={(event) => setEditValues({ ...editValues, paymentTerm: event.target.value })}>{PAYMENT_TERMS.filter((term) => editValues.courseType !== 'self_paced' || term.value === 'full').map((term) => <option key={term.value} value={term.value}>{term.label}</option>)}</select><small>{editValues.courseType === 'self_paced' ? 'Self-paced digital courses are paid in one full installment.' : `Recommended: ${PAYMENT_TERMS.find((term) => term.value === recommendedPaymentTerm(editValues.durationValue, editValues.durationUnit))?.label}`}</small></FormField>
          <label className="course-image-upload" htmlFor="edit-course-image"><span><UploadCloud size={16} /> Course image</span><small>{imageFile ? imageFile.name : 'Use the current image or upload a replacement.'}</small><input id="edit-course-image" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => { const file = event.target.files?.[0] || null; setImageFile(file); if (file) setEditValues({ ...editValues, imageURL: URL.createObjectURL(file) }); }} /></label>
          <div className="course-image-preview"><img src={editValues.imageURL || defaultCourseImage} alt="Course preview" /><span><Image size={14} /> {uploadingImage ? 'Uploading image...' : 'This image appears on your dashboard and public course card.'}</span></div>
          <FormField label="Description" id="edit-course-description"><textarea id="edit-course-description" value={editValues.description} onChange={(event) => setEditValues({ ...editValues, description: event.target.value })} /></FormField>
          <div className="form-actions"><Button type="button" variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button><Button type="submit" loading={saving} icon={Save}>Save changes</Button></div>
        </form>
      </Modal>

      {!isPrivateInstructor ? (
        <section className="panel">
          <div className="panel-heading">
            <h2>Instructor</h2>
            <Users size={18} />
          </div>
          <form onSubmit={assignInstructor} className="settings-form">
            {instructorMembers.length ? (
              <FormField label="Team Member" id="detail-instructor-member">
                <select
                  id="detail-instructor-member"
                  value={assignValues.instructorId}
                  onChange={(event) => {
                    const member = instructorMembers.find((item) => item.userId === event.target.value);
                    setAssignValues({ instructorId: event.target.value, instructorName: member?.displayName || member?.email || '' });
                  }}
                >
                  <option value="">Choose a team member</option>
                  {instructorMembers.map((member) => <option key={member.userId} value={member.userId}>{member.displayName || member.email} ({member.roleId})</option>)}
                </select>
              </FormField>
            ) : null}
            <FormField label="Instructor Name" id="detail-instructor-name">
              <input id="detail-instructor-name" required value={assignValues.instructorName} onChange={(event) => setAssignValues({ ...assignValues, instructorName: event.target.value })} placeholder="Instructor name" />
            </FormField>
            <Button type="submit" loading={saving}>Save Instructor</Button>
          </form>
        </section>
      ) : null}

      <section className="panel">
        <div className="panel-heading">
          <h2>Scheme of Work</h2>
          <p>View, copy, paste and edit the scheme of work week by week.</p>
        </div>
        <div className="prompt-card">
          <div className="prompt-card-header">
            <div>
              <span className="curriculum-step-label">Step 1</span>
              <h3>Prepare your curriculum prompt</h3>
              <p>Tell the AI who you are teaching and what to focus on. SmartCampus will build a ready-to-copy prompt for you.</p>
            </div>
          </div>
          <div className="form-grid">
            <FormField label="Target Learners" id="prompt-target-learners">
              <input
                id="prompt-target-learners"
                value={promptValues.targetLearners}
                onChange={(event) => setPromptValues({ ...promptValues, targetLearners: event.target.value })}
                placeholder="Diploma students, beginners, adult learners..."
              />
            </FormField>
            <FormField label="Focus Areas" id="prompt-focus-areas">
              <input
                id="prompt-focus-areas"
                value={promptValues.focusAreas}
                onChange={(event) => setPromptValues({ ...promptValues, focusAreas: event.target.value })}
                placeholder="Practical skills, assessment, revision..."
              />
            </FormField>
          </div>
          <FormField label="Extra Prompt Instructions" id="prompt-extra-instructions">
            <textarea
              id="prompt-extra-instructions"
              value={promptValues.extraInstructions}
              onChange={(event) => setPromptValues({ ...promptValues, extraInstructions: event.target.value })}
              placeholder="Add anything else you want the AI to consider when generating the JSON."
            />
          </FormField>
          <div className="curriculum-prompt-output">
            <div className="curriculum-prompt-output-heading">
              <div>
                <span>Your ready-to-copy prompt</span>
                <p>Copy this entire prompt, then paste it into ChatGPT or Claude.</p>
              </div>
              <Button type="button" variant="secondary" icon={Copy} onClick={copyCurriculumPrompt}>Copy prompt</Button>
            </div>
            <textarea readOnly value={curriculumPrompt} className="prompt-textarea" aria-label="Curriculum prompt" />
          </div>
        </div>

        <section className="curriculum-import-card">
          <div className="curriculum-import-heading">
            <div className="curriculum-import-step">2</div>
            <div>
              <span className="curriculum-step-label">Step 2</span>
              <h3>Generate and import your scheme of work</h3>
              <p>Use the prompt in ChatGPT or Claude, ask it to generate the JSON, then paste its full response below.</p>
            </div>
          </div>
          <ol className="curriculum-import-steps">
            <li><strong>Copy</strong> the prompt from Step 1.</li>
            <li><strong>Paste it</strong> into ChatGPT or Claude and generate the curriculum.</li>
            <li><strong>Paste the JSON response</strong> below, then import it into SmartCampus.</li>
          </ol>
          <form onSubmit={importCurriculum} className="json-import-form">
            <FormField label="Paste generated JSON" id="curriculum-json-import">
              <textarea
                className="table-input"
                id="curriculum-json-import"
                value={importJsonText}
                onChange={(event) => setImportJsonText(event.target.value)}
                placeholder="Paste the JSON generated by the AI here. It can contain week blocks, days, topic, objective, learning activities, goals, and weekly objective."
              />
            </FormField>
            {jsonError ? <div className="form-error">{jsonError}</div> : null}
            <div className="form-actions">
              <Button type="submit" loading={saving}>Import scheme of work</Button>
            </div>
          </form>
        </section>
        <div className="week-blocks">
          {weekBlocks.length ? weekBlocks.map((week) => (
            <CurriculumWeekTable
              key={week.weekLabel}
              week={week}
              onSaveRow={saveCurriculumBlock}
              onRemoveRow={removeCurriculumEntry}
              onSaveWeekObjective={saveWeekObjective}
              saving={saving}
            />
          )) : (
            <EmptyState title="No scheme of work entries yet." message="Paste curriculum JSON above to generate week blocks from Monday to Friday." icon={CheckCircle2} />
          )}
        </div>
      </section>
    </div>
  );
}
